import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Truck, Shield, Award, Heart } from 'lucide-react';
import useStore from '../store/useStore';
import { translations } from '../i18n/translations';
import { getProducts, getCategories } from '../api/api';
import ProductCard from '../components/ProductCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Helper to resolve image URLs (handles both local uploads and external URLs)
const resolveImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('/api/')) {
    return `${BACKEND_URL}${url}`;
  }
  return url;
};

const HomePage = () => {
  const { language, siteSettings } = useStore();
  const t = translations[language];
  const freeShippingThreshold = siteSettings?.freeShippingThreshold || 75;
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [newArrivals, setNewArrivals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadProducts = async () => {
      try {
        // Load featured products
        const featured = await getProducts({ featured: true, limit: 4 });
        setFeaturedProducts(featured);
        
        // Load new arrivals
        const newItems = await getProducts({ new_arrival: true, limit: 4 });
        setNewArrivals(newItems);
      } catch (error) {
        console.error('Error loading products:', error);
      } finally {
        setLoading(false);
      }
    };
    loadProducts();
  }, []);

  // Default category images (fallback)
  const defaultCategoryImages = {
    sweaters: 'https://images.pexels.com/photos/7671307/pexels-photo-7671307.jpeg?auto=compress&cs=tinysrgb&w=600',
    hoodies: 'https://images.pexels.com/photos/14064288/pexels-photo-14064288.jpeg?auto=compress&cs=tinysrgb&w=600',
    tshirts: 'https://images.unsplash.com/photo-1619021333065-059e44bd1376?w=600',
    jackets: 'https://images.pexels.com/photos/8947416/pexels-photo-8947416.jpeg?auto=compress&cs=tinysrgb&w=600',
  };

  // Generic placeholder for new categories
  const genericPlaceholder = 'https://images.pexels.com/photos/1148957/pexels-photo-1148957.jpeg?auto=compress&cs=tinysrgb&w=600';

  // Get category image from settings or use default
  const getCategoryImage = (categoryKey) => {
    // First check if there's a custom image in site settings (from Personnalisation tab)
    const customImage = siteSettings?.category_images?.[categoryKey];
    if (customImage) {
      return resolveImageUrl(customImage);
    }
    // Then check default images for predefined categories
    if (defaultCategoryImages[categoryKey]) {
      return defaultCategoryImages[categoryKey];
    }
    // Fallback to generic placeholder for new categories
    return genericPlaceholder;
  };

  // Get hero image from settings or use default
  const heroImage = resolveImageUrl(siteSettings?.hero_image) || 'https://images.pexels.com/photos/8947416/pexels-photo-8947416.jpeg?auto=compress&cs=tinysrgb&w=1600';

  // Default categories (fallback)
  const defaultCategories = [
    { key: 'sweaters', image: getCategoryImage('sweaters'), path: '/shop/sweaters' },
    { key: 'hoodies', image: getCategoryImage('hoodies'), path: '/shop/hoodies' },
    { key: 'tshirts', image: getCategoryImage('tshirts'), path: '/shop/tshirts' },
    { key: 'jackets', image: getCategoryImage('jackets'), path: '/shop/jackets' },
  ];

  const [categories, setCategories] = useState(defaultCategories);

  // Load categories from API
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const data = await getCategories(true); // active only
        if (data && Array.isArray(data) && data.length > 0) {
          // Filter to only active categories and map to homepage format
          const activeCats = data
            .filter(cat => cat.active !== false && !cat.legacy)
            .map(cat => {
              if (cat.slug) {
                // Priority: 1. Category's own image, 2. Personnalisation image, 3. Default/placeholder
                let imageUrl = null;
                if (cat.image) {
                  imageUrl = resolveImageUrl(cat.image);
                } else if (siteSettings?.category_images?.[cat.slug]) {
                  imageUrl = resolveImageUrl(siteSettings.category_images[cat.slug]);
                } else {
                  imageUrl = getCategoryImage(cat.slug);
                }
                
                return { 
                  key: cat.slug, 
                  path: `/shop/${cat.slug}`,
                  image: imageUrl,
                  label: cat.translations?.[language]?.name || cat.translations?.fr?.name || cat.slug
                };
              } else if (cat.category) {
                // Legacy format from products aggregation
                return { 
                  key: cat.category, 
                  path: `/shop/${cat.category}`,
                  image: getCategoryImage(cat.category)
                };
              }
              return null;
            })
            .filter(Boolean);
          
          if (activeCats.length > 0) {
            setCategories(activeCats);
          }
        }
      } catch (error) {
        console.error('Error loading categories for homepage:', error);
        // Keep default categories on error
      }
    };
    loadCategories();
  }, [language, siteSettings]);

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative h-[80vh] min-h-[600px] bg-gradient-to-br from-amber-900 via-amber-800 to-amber-950">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{
            backgroundImage: `url(${heroImage})`,
          }}
        />
        <div className="relative h-full flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <div className="max-w-2xl">
              <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
                {t.hero.title}
              </h1>
              <p className="text-xl text-amber-100 mb-8">
                {t.hero.subtitle}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/shop"
                  className="inline-flex items-center justify-center gap-2 bg-white text-amber-900 px-8 py-4 rounded-lg font-semibold hover:bg-amber-50 transition-colors"
                  data-testid="hero-cta"
                >
                  {t.hero.cta}
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
              <div className="mt-8 flex items-center gap-2 text-amber-200">
                <Truck className="w-5 h-5" />
                <span>{language === 'fr' ? `Livraison gratuite dès ${freeShippingThreshold}€` : `Gratis verzending vanaf €${freeShippingThreshold}`}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-12 bg-amber-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center">
                <Truck className="w-7 h-7 text-amber-700" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">
                  {language === 'fr' ? 'Livraison Gratuite' : 'Gratis Verzending'}
                </h3>
                <p className="text-sm text-gray-600">
                  {language === 'fr' ? `Dès ${freeShippingThreshold}€ d'achat` : `Vanaf €${freeShippingThreshold} aankoop`}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center">
                <Shield className="w-7 h-7 text-amber-700" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">
                  {language === 'fr' ? 'Paiement Sécurisé' : 'Veilige Betaling'}
                </h3>
                <p className="text-sm text-gray-600">
                  {language === 'fr' ? 'Transactions protégées' : 'Beschermde transacties'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center">
                <Award className="w-7 h-7 text-amber-700" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">
                  {language === 'fr' ? 'Qualité Premium' : 'Premium Kwaliteit'}
                </h3>
                <p className="text-sm text-gray-600">
                  {language === 'fr' ? 'Matériaux sélectionnés' : 'Geselecteerde materialen'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            {language === 'fr' ? 'Nos Catégories' : 'Onze Categorieën'}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {categories.map((cat) => (
              <Link
                key={cat.key}
                to={cat.path}
                className="group relative aspect-[3/4] overflow-hidden rounded-lg"
              >
                <img
                  src={cat.image}
                  alt={cat.label || t.nav[cat.key] || cat.key}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                <div className="absolute bottom-4 left-4">
                  <h3 className="text-white font-bold text-lg">{cat.label || t.nav[cat.key] || cat.key}</h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      {!loading && featuredProducts.length > 0 && (
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900">
                {t.products.featured}
              </h2>
              <Link
                to="/shop"
                className="text-amber-700 hover:text-amber-800 font-medium flex items-center gap-1"
              >
                {t.products.allProducts}
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {featuredProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* New Arrivals */}
      {!loading && newArrivals.length > 0 && (
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900">
                {t.products.newArrivals}
              </h2>
              <Link
                to="/shop"
                className="text-amber-700 hover:text-amber-800 font-medium flex items-center gap-1"
              >
                {t.products.allProducts}
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {newArrivals.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Newsletter */}
      <section className="py-16 bg-amber-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Heart className="w-12 h-12 text-amber-200 mx-auto mb-4" />
          <h2 className="text-3xl font-bold text-white mb-4">
            {language === 'fr' ? 'Rejoignez la communauté EBB' : 'Word lid van de EBB community'}
          </h2>
          <p className="text-amber-200 mb-8 max-w-2xl mx-auto">
            {language === 'fr'
              ? 'Inscrivez-vous à notre newsletter pour recevoir les dernières nouveautés et offres exclusives.'
              : 'Schrijf je in voor onze nieuwsbrief voor de laatste nieuwtjes en exclusieve aanbiedingen.'}
          </p>
          <div className="max-w-md mx-auto flex">
            <input
              type="email"
              placeholder={t.footer.newsletterPlaceholder}
              className="flex-1 px-4 py-3 rounded-l-lg focus:outline-none"
            />
            <button className="bg-amber-700 hover:bg-amber-600 text-white px-6 py-3 rounded-r-lg font-medium transition-colors">
              {t.footer.subscribe}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
